/**
 * Tooth Chart V1 — surface prefix parser.
 *
 * The backend doesn't yet persist per-surface diagnosis data, but the
 * frontend can preserve surface intent across page reloads by encoding
 * the selected surface(s) as a structured prefix in the diagnosis
 * content text.
 *
 *   Saved content: "[M,O] caries on lingual margin"
 *   Parsed       : { surfaces: ['M','O'], rest: 'caries on lingual margin' }
 *
 * MVP: this is a one-way structured prefix; if a dentist hand-edits the
 * content and drops the bracket, the surfaces disappear. V2 will promote
 * `surfaces` to a first-class field on the NOTE row and the parser will
 * become a fallback.
 */

const VALID_SURFACES = new Set(['M', 'D', 'B', 'L', 'O']);
const PREFIX_REGEX = /^\[([MDBLO,\s]+)\]\s*(.*)$/;


/**
 * Pull a surface tag off the front of a diagnosis content string.
 * Returns { surfaces: string[], rest: string }. If the prefix is
 * malformed or absent, returns { surfaces: [], rest: content }.
 */
export function parseSurfacesFromContent(content) {
  if (!content || typeof content !== 'string') {
    return { surfaces: [], rest: content || '' };
  }
  const match = content.match(PREFIX_REGEX);
  if (!match) return { surfaces: [], rest: content };

  const surfaces = match[1]
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter((s) => VALID_SURFACES.has(s));

  if (surfaces.length === 0) return { surfaces: [], rest: content };

  // Dedupe in case the prefix had repeats like "[M,M,O]".
  const seen = new Set();
  const deduped = [];
  for (const s of surfaces) {
    if (seen.has(s)) continue;
    seen.add(s);
    deduped.push(s);
  }
  return { surfaces: deduped, rest: match[2] };
}


/**
 * Build a content string with the surface prefix applied. If the body
 * already starts with a surface prefix it's stripped first so we don't
 * double-prefix.
 *
 *   composeContentWithSurfaces(['M','O'], 'caries') → '[M,O] caries'
 *   composeContentWithSurfaces([], 'caries')        → 'caries'
 */
export function composeContentWithSurfaces(surfaces, body) {
  const { rest } = parseSurfacesFromContent(body);
  const cleanBody = (rest || '').trimStart();
  if (!surfaces || surfaces.length === 0) return cleanBody;

  // Preserve the M/D/B/L/O canonical order regardless of click order.
  const order = ['M', 'D', 'B', 'L', 'O'];
  const sorted = order.filter((s) => surfaces.includes(s));
  if (sorted.length === 0) return cleanBody;

  return `[${sorted.join(',')}]${cleanBody ? ' ' : ''}${cleanBody}`;
}
